import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Avatar, Typography, Chip, CircularProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SearchBar from '../../components/SearchBar';
import DataTable from '../../components/DataTable';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatCurrency } from '../../utils/formatters';
import { getPoojaTypes, deletePoojaType } from '../../api/endpoints/pooja';
import AddPoojaModal from './AddPoojaModal';
import { API_URL } from '../../utils/url';

const PoojaList = () => {
  const [poojas, setPoojas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editPooja, setEditPooja] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPoojas = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (catFilter !== 'all') params.category = catFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getPoojaTypes(params);
      setPoojas(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      if (err.response?.status !== 404) toast.error(err.response?.data?.message || 'Failed to load pooja types');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, catFilter, debouncedSearch]);

  useEffect(() => { fetchPoojas(); }, [fetchPoojas]);

  const categories = [...new Set(poojas.map((p) => p.category))];

  const handleDelete = async () => {
    try {
      await deletePoojaType(deleteId);
      toast.success('Pooja type deleted');
      setDeleteId(null);
      fetchPoojas();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete pooja type');
    }
  };

  const handleEdit = (pooja) => {
    setEditPooja(pooja);
    setAddOpen(true);
  };

  const handleAddNew = () => {
    setEditPooja(null);
    setAddOpen(true);
  };

  const handleCloseModal = () => {
    setAddOpen(false);
    setEditPooja(null);
  };

  const handleSaved = () => {
    fetchPoojas();
    handleCloseModal();
  };

  const columns = [
    { id: 'image', label: 'Image', render: (row) => (
      <Avatar src={API_URL?.API_BASE_URL+ row.image} variant="rounded" sx={{ width: 44, height: 44 }}>
        {row.name?.[0]?.toUpperCase()}
      </Avatar>
    )},
    { id: 'name', label: 'Pooja', sortable: true, render: (row) => (
      <Box>
        <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
        <Typography variant="caption" color="text.secondary">{row.typeId}</Typography>
      </Box>
    )},
    { id: 'category', label: 'Category', render: (row) => (
      row.category ? <Chip label={row.category} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /> : '-'
    )},
    { id: 'price', label: 'Price', align: 'right', render: (row) => (
      <Typography variant="body2" fontWeight={600}>{formatCurrency(row.price)}</Typography>
    )},
    { id: 'duration', label: 'Duration', render: (row) => row.duration || '-' },
    { id: 'status', label: 'Status', render: (row) => <StatusChip status={row.isActive ? 'active' : 'inactive'} /> },
    { id: 'actions', label: '', render: (row) => (
      <Stack direction="row">
        <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(row._id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
    )},
  ];

  return (
    <Box>
      <PageHeader title="Pooja Types" subtitle="Manage all puja/pooja types" actionLabel="Add Pooja" onAction={handleAddNew} />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <SearchBar value={search} onChange={setSearch} placeholder="Search poojas..." sx={{ minWidth: 300 }} />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Category</InputLabel>
          <Select value={catFilter} label="Category" onChange={(e) => { setCatFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All</MenuItem>
            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <DataTable
          columns={columns} rows={poojas} page={page} rowsPerPage={rowsPerPage}
          total={total} onPageChange={setPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          emptyMessage="No pooja types found"
        />
      )}
      <ConfirmDialog open={!!deleteId} title="Delete Pooja Type" message="Are you sure you want to delete this pooja type?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      <AddPoojaModal open={addOpen} onClose={handleCloseModal} onSaved={handleSaved} editData={editPooja} />
    </Box>
  );
};

export default PoojaList;
